import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Card, CardContent, Typography, Button, TextField, Grid } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiUrl, recaptchaSite } from '../config';
import { CustomerBookingEligibilityDto, CustomerVehicleValidationDto } from '../interfaces/appointments';
import { saveBookingAccessToken } from './bookingAccess';

const TIRE_HOTEL_BOOKING_MESSAGE = 'Timebestilling er kun tilgjengelig for kunder med dekkhotell. Ring oss for booking på 73 90 47 10.';

declare global {
    interface Window {
        grecaptcha: any;
    }
}

const AppointmentBookingLogin = () => {
    const [telefonnummer, setTelefonnummer] = useState('');
    const [registreringsnummer, setRegistreringsnummer] = useState('');
    const [recaptchaOk, setRecaptchaOk] = useState(false);
    const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [validatedVehicle, setValidatedVehicle] = useState<CustomerVehicleValidationDto | null>(null);
    const [validatingVehicle, setValidatingVehicle] = useState(false);
    const recaptchaRef = useRef<HTMLDivElement | null>(null);
    const recaptchaWidgetIdRef = useRef<number | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as { message?: string } | null;
    const loginMessage = locationState?.message ?? null;

    useEffect(() => {
        const interval = setInterval(() => {
            if (!recaptchaSite) {
                setRecaptchaError('reCAPTCHA-sitekey mangler i miljøvariabler.');
                clearInterval(interval);
                return;
            }

            if (window.grecaptcha && recaptchaRef.current && recaptchaWidgetIdRef.current === null) {
                recaptchaWidgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
                    sitekey: recaptchaSite,
                    callback: verifyCallback,
                    'expired-callback': onRecaptchaExpired,
                    'error-callback': onRecaptchaError,
                });
                clearInterval(interval);
            }
        }, 100);

        return () => {
            clearInterval(interval);
        };
    }, []);

    const handleTelefonnummerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTelefonnummer(event.target.value);
        setValidatedVehicle(null);
        setValidationError(null);
    };
    const handleRegistreringsnummerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRegistreringsnummer(event.target.value);
        setValidatedVehicle(null);
        setValidationError(null);
    };

    const validateBookingEligibility = async (): Promise<boolean> => {
        const normalizedPhone = telefonnummer.trim();
        const normalizedRegistrationNumber = registreringsnummer.trim().toUpperCase();

        if (!normalizedPhone || !normalizedRegistrationNumber) {
            setValidationError('Telefonnummer og registreringsnummer må fylles ut.');
            setValidatedVehicle(null);
            return false;
        }

        setValidatingVehicle(true);
        setValidationError(null);

        try {
            const eligibilityResponse = await axios.get<CustomerBookingEligibilityDto>(
                `${apiUrl}/appointments/customer/${normalizedPhone}/${normalizedRegistrationNumber}/eligibility`,
            );
            if (!eligibilityResponse.data.hasTireHotel) {
                setValidatedVehicle(null);
                setValidationError(TIRE_HOTEL_BOOKING_MESSAGE);
                return false;
            }

            const response = await axios.get<CustomerVehicleValidationDto>(
                `${apiUrl}/appointments/customer/${normalizedPhone}/${normalizedRegistrationNumber}/vehicle/validation`,
            );
            setValidatedVehicle(response.data);
            return true;
        } catch (err: any) {
            const errorMessage =
                typeof err?.response?.data === 'string' && err.response.data.trim().length > 0
                    ? err.response.data
                    : 'Kunne ikke validere registreringsnummer mot SVV akkurat nå. Prøv igjen senere eller ring oss på 73 90 47 10 for å bestille time.';
            setValidatedVehicle(null);
            setValidationError(errorMessage);
            return false;
        } finally {
            setValidatingVehicle(false);
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const normalizedPhone = telefonnummer.trim();
        const normalizedRegistrationNumber = registreringsnummer.trim().toUpperCase();

        const isVehicleValid = await validateBookingEligibility();
        if (!isVehicleValid) {
            return;
        }

        if (!recaptchaOk) {
            setRecaptchaError('Fullfør reCAPTCHA før du fortsetter.');
            return;
        }

        saveBookingAccessToken(
            normalizedPhone,
            normalizedRegistrationNumber,
            'session-gate',
            new Date(Date.now() + 30 * 60 * 1000).toISOString(),
        );

        navigate(`/timebestilling/${normalizedPhone}/${normalizedRegistrationNumber}`);
    };
    type VerifyCallback = (response: string) => void;

    const verifyCallback: VerifyCallback = (_response) => {
        setRecaptchaOk(true);
        setRecaptchaError(null);
    };

    const onRecaptchaExpired = () => {
        setRecaptchaOk(false);
    };

    const onRecaptchaError = () => {
        setRecaptchaOk(false);
        setRecaptchaError('reCAPTCHA fikk timeout eller nettverksfeil. Prøv igjen.');
    };

    return (
        <Grid container justifyContent="center" >
            <form onSubmit={handleSubmit}>
                <Grid item xs={12} sm={10}>
                    <Card>
                        <CardContent >
                            <Typography variant='h4' color="text.secondary" gutterBottom>
                                Logg inn for å bestille time
                            </Typography>

                            <Typography variant="body2" margin={1}>
                                Skriv inn ditt telefonnummer (8 siffer) og kjøretøyets registreringsnummer, og verifiser at du ikke er en robot.
                            </Typography>
                            {loginMessage && (
                                <Alert severity="info" sx={{ mb: 1 }}>
                                    {loginMessage}
                                </Alert>
                            )}

                            <TextField margin='normal'
                                label="Telefonnummer"
                                value={telefonnummer}
                                onChange={handleTelefonnummerChange}
                                onBlur={() => {
                                    if (telefonnummer.trim() && registreringsnummer.trim()) {
                                        void validateBookingEligibility();
                                    }
                                }}
                                inputProps={{ maxLength: 8 }}
                                required
                            />
                            <TextField
                                margin='normal'
                                label="Registreringsnummer"
                                value={registreringsnummer}
                                onChange={handleRegistreringsnummerChange}
                                onBlur={() => {
                                    if (telefonnummer.trim() && registreringsnummer.trim()) {
                                        void validateBookingEligibility();
                                    }
                                }}
                                inputProps={{ maxLength: 9 }}
                                required
                            />
                            {validatedVehicle && (
                                <Alert severity="success" sx={{ mb: 1 }}>
                                    Kjøretøy validert: {validatedVehicle.brand} {validatedVehicle.model} ({validatedVehicle.registrationNumber})
                                </Alert>
                            )}
                            {validationError && (
                                <Alert severity="error" sx={{ mb: 1 }}>
                                    {validationError}
                                </Alert>
                            )}
                            <div>
                                <div ref={recaptchaRef}></div>
                                <Button type="submit" variant="contained" color="primary" disabled={!recaptchaOk || validatingVehicle}>
                                    {validatingVehicle ? 'Validerer kjøretøy...' : 'Gå til timebestilling'}
                                </Button>
                                {recaptchaError && (
                                    <Typography variant="body2" color="error" mt={1}>
                                        {recaptchaError}
                                    </Typography>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </Grid>
            </form>
        </Grid>
    );
};

export default AppointmentBookingLogin;
